import { LegendStates, MouseSelectionStates, SelectedParams } from "types";
import { SeriesType } from "types/echarts";

import {
  getAttentionWellIndex,
  getGroupName,
  getLegendId,
  getSeriesIndex,
  getSeriesIndexFromSelectedGroups,
  getSeriesName,
  getSeriesNameUnfiltered,
  isValidChartLegendStates
} from "./utils";

/**
 * Strips the product name (and optional forecast suffix) from a series label.
 * e.g. '(1700) Montney (Oil + Cond) Forecast (EVA Research)' → '(1700) Montney'
 *
 * Used in MPC mode because chart and focus bins have no product-level granularity,
 * so hover legends should not either.
 */
export function stripProductSuffix(name: string): string {
  const stripped = name?.replace(/\s*\([^)]*\)(?:\s+Forecast\s+\([^)]*\))?$/, "").trim();
  return stripped || name || "";
}

export function mpcCompatibleHoverLegends({
  params,
  series,
  forecast,
  isMultiProductActive
}: Partial<MouseSelectionStates>) {
  const seriesName = getSeriesName({ params, series, forecast });
  const groupName = getGroupName({ params, series });
  const legendId = getLegendId({ params, series });
  const unfilteredSeriesName = getSeriesNameUnfiltered({ params, series });

  const resolvedSeriesName = isMultiProductActive
    ? stripProductSuffix(seriesName)
    : seriesName;
  const resolvedGroupName = isMultiProductActive
    ? stripProductSuffix(groupName)
    : groupName;

  return {
    hoverLegendItem: resolvedSeriesName,
    hoverLegendGroup: resolvedGroupName,
    hoverLegendId: legendId,
    hoverLegendItemUnfiltered: unfilteredSeriesName
  };
}

export function mpcCompatibleSelectionParams({
  states,
  series
}: Partial<LegendStates>): SelectedParams {
  if (!states || isValidChartLegendStates({ states, series })) {
    return { seriesIndex: [], targetIndex: undefined, seriesType: SeriesType.Line };
  }

  const { hoverLegendGroup, hoverLegendItem, attentionWells, selectedGroups } = states;

  const isHoveringOnBins =
    hoverLegendItem && !hoverLegendGroup && attentionWells?.length === 0;
  const isHoveringOnChart =
    hoverLegendItem && hoverLegendGroup && hoverLegendGroup !== hoverLegendItem;
  const isHoveringOnMap =
    hoverLegendItem && !hoverLegendGroup && attentionWells?.length > 0;

  const onlySelectedGroup =
    !hoverLegendItem &&
    !hoverLegendGroup &&
    attentionWells?.length === 0 &&
    selectedGroups?.length > 0;

  const selectedGroupsIndices = getSeriesIndexFromSelectedGroups({ states, series });

  const seriesIndex = getSeriesIndex({ states, series });

  if (isHoveringOnBins) {
    return {
      seriesIndex: [...seriesIndex, ...selectedGroupsIndices],
      seriesType: SeriesType.Line,
      targetIndex: undefined
    };
  }

  if (isHoveringOnChart) {
    return {
      seriesIndex: [...seriesIndex, ...selectedGroupsIndices],
      targetIndex: seriesIndex,
      seriesType: SeriesType.Line
    };
  }

  if (isHoveringOnMap) {
    return {
      seriesIndex: [...seriesIndex, ...selectedGroupsIndices],
      targetIndex: getAttentionWellIndex({ states, series }),
      seriesType: SeriesType.Line
    };
  }

  if (onlySelectedGroup) {
    return {
      seriesIndex: [...selectedGroupsIndices],
      targetIndex: undefined,
      seriesType: SeriesType.Line
    };
  }

  return { seriesIndex: [], targetIndex: undefined, seriesType: SeriesType.Line };
}
